import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Star, Check, Clock } from "lucide-react";
import { ServiceIcon } from "@/components/site/icons";

/* ---------------------------------------------------------------- Service */
export function ServiceCard({ service, index = 0 }) {
  return (
    <Link
      to={`/services/${service.slug}`}
      data-testid={`service-card-${service.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition-colors duration-300 hover:border-vantage/50"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-vantage/20 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-vantage/10 text-vantage transition-colors duration-300 group-hover:bg-vantage group-hover:text-white">
        <ServiceIcon name={service.icon} />
      </div>
      <h3 className="mt-6 font-heading text-xl font-semibold text-white">{service.title}</h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{service.short_desc}</p>
      <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-vantage">
        Learn more
        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </Link>
  );
}

/* -------------------------------------------------------------- Portfolio */
export function PortfolioCard({ item, index = 0 }) {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className="group relative"
      data-testid={`portfolio-card-${item.slug}`}
    >
      <Link to={`/portfolio/${item.slug}`} className="block overflow-hidden rounded-3xl border border-white/10">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/20 to-transparent" />
          <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-ink-950/60 px-3 py-1 text-xs font-medium text-white backdrop-blur">
            {item.category}
          </span>
        </div>
        <div className="flex items-end justify-between p-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">{item.client}</p>
            <h3 className="mt-1 font-heading text-xl font-semibold text-white">{item.title}</h3>
          </div>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-white transition-colors group-hover:bg-vantage group-hover:border-vantage">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

/* ------------------------------------------------------------ Testimonial */
export function TestimonialCard({ t }) {
  return (
    <div className="flex h-full flex-col rounded-3xl border border-white/10 bg-white/[0.03] p-8" data-testid="testimonial-card">
      <div className="flex gap-1">
        {Array.from({ length: t.rating || 5 }).map((_, i) => (
          <Star key={i} className="h-4 w-4 fill-vantage text-vantage" />
        ))}
      </div>
      <p className="mt-6 flex-1 text-base leading-relaxed text-slate-200">“{t.quote}”</p>
      <div className="mt-8 flex items-center gap-3">
        <img src={t.avatar} alt={t.name} loading="lazy" className="h-11 w-11 rounded-full object-cover" />
        <div>
          <p className="font-heading text-sm font-semibold text-white">{t.name}</p>
          <p className="text-xs text-slate-400">{t.role}, {t.company}</p>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- Blog */
export function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] transition-colors duration-300 hover:border-vantage/40"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img src={post.cover_image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="rounded-full bg-vantage/10 px-2.5 py-1 font-medium text-vantage">{post.category}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{post.read_time} min read</span>
        </div>
        <h3 className="mt-4 font-heading text-lg font-semibold text-white transition-colors group-hover:text-vantage">{post.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{post.excerpt}</p>
        <span className="mt-4 text-xs text-slate-500">By {post.author}</span>
      </div>
    </Link>
  );
}

/* ----------------------------------------------------------------- Pricing */
export function PricingCard({ plan }) {
  const priceLabel = plan.period === "custom" ? "Custom" : `$${plan.price.toLocaleString()}`;
  return (
    <div
      data-testid={`pricing-card-${plan.name.toLowerCase()}`}
      className={`relative flex flex-col rounded-3xl border p-8 ${
        plan.highlighted ? "border-vantage/60 bg-vantage/[0.06] glow-blue" : "border-white/10 bg-white/[0.03]"
      }`}
    >
      {plan.highlighted && (
        <span className="absolute -top-3 left-8 rounded-full bg-vantage px-3 py-1 text-xs font-semibold text-white">
          Most Popular
        </span>
      )}
      <h3 className="font-heading text-xl font-semibold text-white">{plan.name}</h3>
      <p className="mt-2 text-sm text-slate-400">{plan.description}</p>
      <div className="mt-6 flex items-end gap-1">
        <span className="font-heading text-4xl font-bold text-white">{priceLabel}</span>
        {plan.period !== "custom" && <span className="mb-1 text-sm text-slate-500">/{plan.period}</span>}
      </div>
      <ul className="mt-8 space-y-3">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-vantage" />
            {f}
          </li>
        ))}
      </ul>
      <Link
        to="/contact"
        className={`mt-8 w-full rounded-full py-3.5 text-center text-sm font-semibold transition-colors ${
          plan.highlighted ? "bg-vantage text-white hover:bg-vantage-hover" : "border border-white/15 text-white hover:bg-white/5"
        }`}
      >
        Get started
      </Link>
    </div>
  );
}
